import { KJUR } from 'jsrsasign';

interface TeamMember {
  name: string;
  email: string;
  githubUsername?: string;
  linkedinUrl?: string;
}

interface TeamRegistrationData {
  teamName: string;
  teamSize: string;
  teamMembers: TeamMember[];
}

interface ProjectSubmissionData {
  teamName: string;
  projectName: string;
  description: string;
  githubRepo: string;
  demoLink: string;
  teamMembers: string;
  challenges: string;
  learnings: string;
}

const SPREADSHEET_ID = import.meta.env.VITE_GOOGLE_SHEET_ID;
const SERVICE_ACCOUNT_EMAIL = import.meta.env.VITE_GOOGLE_SERVICE_ACCOUNT_EMAIL;
const PRIVATE_KEY = import.meta.env.VITE_GOOGLE_PRIVATE_KEY?.replace(/\\n/g, '\n');

// Validate environment variables
if (!SPREADSHEET_ID || SPREADSHEET_ID === 'your-spreadsheet-id') {
  console.error('Invalid or missing VITE_GOOGLE_SHEET_ID');
  throw new Error('Google Sheets ID is not configured');
}

if (!SERVICE_ACCOUNT_EMAIL) {
  console.error('Missing VITE_GOOGLE_SERVICE_ACCOUNT_EMAIL');
  throw new Error('Service account email is not configured');
}

if (!PRIVATE_KEY) {
  console.error('Missing VITE_GOOGLE_PRIVATE_KEY');
  throw new Error('Private key is not configured');
}

// Log specific environment variables
console.log('Using configuration:', {
  SPREADSHEET_ID,
  SERVICE_ACCOUNT_EMAIL,
  PRIVATE_KEY_EXISTS: !!PRIVATE_KEY,
  PRIVATE_KEY_LENGTH: PRIVATE_KEY?.length
});

// Format the private key for jsrsasign
function formatPrivateKey(key: string): string {
  try {
    console.log('Original key:', key);
    
    // Remove any existing headers/footers
    let formattedKey = key.replace(/-----BEGIN PRIVATE KEY-----/, '');
    formattedKey = formattedKey.replace(/-----END PRIVATE KEY-----/, '');
    formattedKey = formattedKey.replace(/\s/g, '');
    
    // Add proper headers/footers
    formattedKey = `-----BEGIN PRIVATE KEY-----\n${formattedKey}\n-----END PRIVATE KEY-----`;
    
    console.log('Formatted key:', formattedKey);
    return formattedKey;
  } catch (error) {
    console.error('Error formatting private key:', error);
    throw error;
  }
}

export async function registerTeam(data: TeamRegistrationData) {
  try {
    console.log('Starting team registration...');
    console.log('Using spreadsheet ID:', SPREADSHEET_ID);

    const values = [
      [
        new Date().toISOString(),
        data.teamName,
        data.teamSize,
        ...data.teamMembers.flatMap((member) => [
          member.name,
          member.email,
          member.githubUsername || '',
          member.linkedinUrl || ''
        ])
      ]
    ];

    console.log('Generated values:', values);

    const accessToken = await getAccessToken();
    console.log('Got access token:', accessToken ? 'Yes' : 'No');

    const url = `https://sheets.googleapis.com/v4/spreadsheets/${SPREADSHEET_ID}/values/Team%20Registrations!A:Z:append?valueInputOption=RAW`;
    console.log('Making request to:', url);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({
        values,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Google Sheets API Error:', errorData);
      throw new Error('Failed to register team');
    }

    return { success: true, message: 'Team registered successfully' };
  } catch (error) {
    console.error('Error registering team:', error);
    throw new Error('Failed to register team');
  }
}

export async function submitProject(data: ProjectSubmissionData) {
  try {
    const values = [
      [
        new Date().toISOString(),
        data.teamName,
        data.projectName,
        data.description,
        data.githubRepo,
        data.demoLink,
        data.teamMembers,
        data.challenges,
        data.learnings
      ]
    ];

    const accessToken = await getAccessToken();
    console.log('Got access token for project submission:', accessToken ? 'Yes' : 'No');

    const response = await fetch(
      `https://sheets.googleapis.com/v4/spreadsheets/${SPREADSHEET_ID}/values/Project%20Submissions!A:Z:append?valueInputOption=RAW`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          values,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Google Sheets API Error:', errorData);
      throw new Error('Failed to submit project');
    }

    return { success: true, message: 'Project submitted successfully' };
  } catch (error) {
    console.error('Error submitting project:', error);
    throw new Error('Failed to submit project');
  }
}

async function getAccessToken() {
  try {
    console.log('Getting access token...');
    const jwt = generateJWT();
    console.log('Generated JWT:', jwt ? 'Yes' : 'No');

    const response = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        grant_type: 'urn:ietf:params:oauth:grant-type:jwt-bearer',
        assertion: jwt,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('OAuth Error:', errorData);
      throw new Error('Failed to get access token');
    }

    const data = await response.json();
    return data.access_token;
  } catch (error) {
    console.error('Error getting access token:', error);
    throw error;
  }
}

function generateJWT() {
  try {
    console.log('Generating JWT...');
    const now = Math.floor(Date.now() / 1000);
    const header = { alg: 'RS256', typ: 'JWT' };
    const payload = {
      iss: SERVICE_ACCOUNT_EMAIL,
      scope: 'https://www.googleapis.com/auth/spreadsheets',
      aud: 'https://oauth2.googleapis.com/token',
      exp: now + 3600,
      iat: now,
    };

    console.log('JWT payload:', payload);

    if (!PRIVATE_KEY) {
      throw new Error('Private key is not defined');
    }

    const formattedKey = formatPrivateKey(PRIVATE_KEY);
    console.log('Using formatted key for signing');

    const jwt = KJUR.jws.JWS.sign(
      'RS256',
      header,
      payload,
      formattedKey
    );

    console.log('JWT generated successfully');
    return jwt;
  } catch (error) {
    console.error('Error generating JWT:', error);
    throw error;
  }
} 